import React, { useCallback, useEffect, useState } from "react";
import { Card, ListGroup } from "react-bootstrap";
import { Location, User, Session, SettingsDoc, Visit } from "../../types/Types";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "@firebase/firestore";
import { db, functions } from "../../../firebase/FirebaseProvider";
import LocationNav from "./LocationNav";
import LocationDetails from "./LocationDetails";
import LocationDetailsV2 from "./LocationDetailsV2";
import { NOTIFICATION_LIMIT, logDataLoaded } from "../../util/Util";
import LocationCheckIn from "./LocationCheckIn";
import SessionRow from "../../shared/SessionRow";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams} from "react-router-dom";
import {httpsCallable} from "@firebase/functions";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import UserTag from "../../users/tag/UserTag";
import DateTime from "../../date/DateTime";

interface Props {
    currentUser: User | undefined,
    usersMap: Map<string, User> | undefined
    settings: SettingsDoc
    likesMap: Map<string, boolean>
}

function LocationSessions(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [sessions, setSessions] = useState<Array<Session> | undefined>(undefined);
    const [location, setLocation] = useState<Location | undefined>(undefined);
    const [visited, setVisited] = useState<boolean>(false);

    const isUserSocial = useCallback((visitId: string): boolean => {
        if(!props.usersMap) {
            return false;
        }
        let user: User | undefined = props.usersMap.get(visitId);
        if(!user) {
            return false;
        }
        return !user.socialDisabled;
    }, [props.usersMap]);

    const loadAllData = useCallback(async() => {
        if(!location) {
            return;
        }
        // Load location sessions
        let sessions: Array<Session> = [];
        const sessionOnceCollection = query(collection(db, 'sessions'),
            where('locationId', '==', location.id),
            orderBy('checkInTime', 'desc'),
            limit(NOTIFICATION_LIMIT));
        const sessionOnceCollectionSnapshot = await getDocs(sessionOnceCollection);
        sessionOnceCollectionSnapshot.forEach((doc) => {
            let session = doc.data() as Session;
            if(isUserSocial(session.userId)) {
                sessions.push(session);
            }
        });
        logDataLoaded('location sessions', sessions.length);
        setSessions(sessions);
    }, [location, isUserSocial]);

    useEffect(() => {
        if (!id) return;
        
        (async () => {
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!id || !props.currentUser?.id) return;
        
        (async () => {
            const visitQuery = query(
                collection(db, 'visits'),
                where('locationId', '==', id),
                where('userId', '==', props.currentUser!.id)
            );
            const visitSnap = await getDocs(visitQuery);
            if (!visitSnap.empty) {
                const visitData = visitSnap.docs[0].data() as Visit;
                setVisited(visitData.visited || false);
            }
        })();
    }, [id, props.currentUser?.id]);

    const toggleVisited = useCallback(() => {
        if (!id) return;
        
        const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
        toggleVisitedFunction({locationId: id}).then(async (response) => {
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
            
            setVisited(prev => !prev);
        }).catch((error) => {
            console.error('Error toggling visited:', error);
            alert('Failed to update visit status');
        });
    }, [id]);

    useEffect(() => {
        if (!location) return;
        loadAllData();
    }, [location, loadAllData]);

    const isUiV2 = !!props.currentUser?.enableUiV2;

    if (isUiV2) {
        return (
            <>
                <PageTracking />
                {props.currentUser && <LocationDetailsV2
                    location={location}
                    currentUser={props.currentUser}
                    likesMap={props.likesMap}
                    visited={visited}
                    onToggleVisited={toggleVisited}
                    variant="header"
                />}
                {location && <V2GlassPanel className="location-v2-detail-toolbar">
                    <Link className="location-v2-back-button" to={`/location/${location.id}/comments`}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Pub
                    </Link>
                    <div className="location-v2-detail-stats">
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Check-ins</span>
                            <b>{sessions?.length || 0}</b>
                        </V2GlassSurface>
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Unique</span>
                            <b>{location.sessionUniqueCount || 0}</b>
                        </V2GlassSurface>
                    </div>
                </V2GlassPanel>}
                <V2GlassPanel className="location-v2-detail-list-card">
                    {sessions && props.usersMap && sessions.map((session: Session) => {
                        const sessionUser = props.usersMap?.get(session.userId);
                        return (
                            <V2GlassSurface key={session.id} className="location-v2-visit-row">
                                <div>
                                    {sessionUser && <UserTag
                                        user={sessionUser}
                                        visit={undefined}
                                        session={session}
                                        padRight={false}
                                        hideLocationDot={false}
                                        linksToTab={"sessions"}
                                        displayBonus={session.bonus || false}
                                    />}
                                </div>
                                <V2GlassChip className="location-v2-visit-date-chip">
                                    <DateTime date={session.checkInTime}/>
                                </V2GlassChip>
                            </V2GlassSurface>
                        );
                    })}
                    {!sessions && <div className="location-v2-empty-list">Loading...</div>}
                    {sessions && sessions.length === 0 && <div className="location-v2-empty-list">No check-ins yet</div>}
                </V2GlassPanel>
            </>
        );
    }

    return (
        <>
            <PageTracking />
            {props.currentUser && (props.currentUser.enableUiV2 ? <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="header"
            /> : <LocationDetails
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
            />)}
            {location && props.currentUser && !props.currentUser.enableUiV2 && !props.currentUser.socialDisabled && <LocationCheckIn currentUser={props.currentUser} location={location}/>}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="location"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="tags"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="widgets"
            />}
            {location && <LocationNav settings={props.settings} currentUser={props.currentUser} locationId={location.id}/>}
            <Card>
                {location && sessions && sessions.length > 0 && <Card.Header className="header-padding">
                    <div className={"d-inline-block stats-card float-left mr-2"}>
                        <span>Total: <b>{sessions.length}</b></span>
                    </div>
                    <div className={"d-inline-block stats-card float-left"}>
                        <span>Unique: <b>{location.sessionUniqueCount}</b></span>
                    </div>
                </Card.Header>}
                <ListGroup variant="flush">
                    {sessions && location && sessions.map((session: Session) => (
                        props.usersMap && <SessionRow 
                            key={session.id}
                            displayName={true}
                            displayLocation={false}
                            session={session}
                            user={props.usersMap.get(session.userId)}
                            displayBonus={session.bonus || false} 
                        />
                    ))}
                    {!sessions && 
                        <div className={"loading"}>Loading...</div>
                    }
                    {(sessions && sessions.length === 0) && 
                        <div className={'loading'}>
                            No check-ins yet
                        </div>
                    }
                </ListGroup>
            </Card>
        </>
    );
}

export default LocationSessions;
